import React, { useState, useEffect } from 'react';
import { Music2, Radio, Disc3 } from 'lucide-react';
import { getAboutSection, getAboutFeatures } from '../services/api';
import { PageLayout } from './layouts/PageLayout';
import { AboutFeatureModal } from './AboutFeatureModal';

const iconMap = {
  Music2: Music2,
  Radio: Radio,
  Disc3: Disc3,
};

export const About = () => {
  const [aboutSection, setAboutSection] = useState(null);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleElements, setVisibleElements] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchAboutData();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements((prev) => [...new Set([...prev, entry.target.dataset.index])]);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [features]);

  const fetchAboutData = async () => {
    try {
      const [sectionRes, featuresRes] = await Promise.all([
        getAboutSection(),
        getAboutFeatures()
      ]);
      setAboutSection(sectionRes.data);
      setFeatures(featuresRes.data);
    } catch (error) {
      console.error('Failed to fetch about data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-2xl text-orange-400 animate-pulse">Loading...</div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout primaryColor="orange" secondaryColor="green">
      <div className="container mx-auto px-4 pt-20 md:pt-24 pb-16 md:pb-24 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div 
            className="text-center mb-12 md:mb-16 transition-all duration-700 ease-out"
            data-animate
            data-index="header"
            style={{
              opacity: visibleElements.includes('header') ? 1 : 0,
              transform: visibleElements.includes('header') ? 'translateY(0)' : 'translateY(30px)'
            }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400 hover:scale-105 transition-transform duration-300 cursor-default inline-block leading-tight pb-2">
              {aboutSection?.title || 'About DharmaR'}
            </h1>
            <div className="w-20 md:w-24 h-1 bg-gradient-to-r from-orange-500 to-green-500 mx-auto mb-4 md:mb-6"></div>
            <p className="text-lg md:text-xl lg:text-2xl text-gray-300 mb-3 md:mb-4 hover:text-gray-200 transition-colors duration-300">
              {aboutSection?.subtitle || 'Explore the depths of consciousness through sound'}
            </p>
            <p className="text-sm md:text-base text-gray-400 leading-relaxed max-w-2xl mx-auto hover:text-gray-300 transition-colors duration-300">
              {aboutSection?.description || 'DharmaR is a psychedelic trance label dedicated to exploring the depths of consciousness through sound.'}
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => {
              const IconComponent = iconMap[feature.icon] || Music2;
              return (
                <div
                  key={feature.id}
                  data-animate
                  data-index={`feature-${index}`}
                  onClick={() => {
                    setSelectedFeature(feature);
                    setIsModalOpen(true);
                  }}
                  className="bg-black/50 backdrop-blur-sm border border-orange-500/30 rounded-lg p-6 md:p-8 hover:border-green-500 hover:shadow-xl hover:shadow-green-500/20 transition-all duration-300 group cursor-pointer"
                  style={{
                    opacity: visibleElements.includes(`feature-${index}`) ? 1 : 0,
                    transform: visibleElements.includes(`feature-${index}`) ? 'translateY(0)' : 'translateY(30px)',
                    transition: `all 0.6s ease-out ${index * 0.1}s`
                  }}
                >
                  <div className="flex justify-center mb-4 md:mb-6">
                    <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-orange-500/20 to-green-500/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-6 h-6 md:w-8 md:h-8 text-orange-400 group-hover:text-green-400 transition-colors duration-300" />
                    </div>
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-center mb-3 md:mb-4 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400 group-hover:scale-105 transition-transform duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-400 text-center leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Feature Details Modal */}
      <AboutFeatureModal 
        feature={selectedFeature}
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedFeature(null);
        }}
      />
    </PageLayout>
  );
};
